import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Form, Spinner, Button } from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import Navbar from "../Components/NavMenu";
import "../App.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import ScrollToTopButton from "../Components/ScrollToTopButton";
import analyticsPdf from "../files/MCQ Assessment Analytics Explained.pdf";
import "../Styles/Analytics.css";

const ITEMS_PER_PAGE = 10;

function Analytics() {
  const [assessments, setAssessments] = useState([]);
  const [assessmentId, setAssessmentId] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [analyticsData, setAnalyticsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedQuestionId, setSelectedQuestionId] = useState("");

  const [distractorLoading, setDistractorLoading] = useState(false);
  const [distractorData, setDistractorData] = useState([]);

  const [summary, setSummary] = useState({
    total_questions: 0,
    total_responses: 0,
    total_participants: 0,
    hard_count: 0,
    moderate_count: 0,
    easy_count: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchAssessmentOptions();
  }, []);

  useEffect(() => {
    if (assessmentId) {
      fetchAnalytics(assessmentId);
      fetchDistractorAnalysis(assessmentId);
    }
  }, [assessmentId]);

  const fetchAssessmentOptions = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getAllAssessment`,
      );
      if (res.data?.success) {
        const list = res.data.data || [];
        setAssessments(list);

        if (list.length > 0) {
          setAssessmentId(String(list[0].assessment_id));
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
        setErrorMessage("Assessments could not be loaded.");
      }
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setLoading(false);
      setErrorMessage("Assessments could not be loaded. Please try again.");
    }
  };

  const fetchAnalytics = async (id) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const res = await axios.post(
        `${process.env.REACT_APP_API}/getDiscrimination`,
        {
          assessment_id: id,
        },
      );

      if (res.data?.success) {
        const rows = res.data.data || [];
        const apiSummary = res.data.summary || {};

        setAnalyticsData(rows);
        setSummary({
          total_questions: Number(apiSummary.total_questions || 0),
          total_responses: Number(apiSummary.total_responses || 0),
          total_participants: Number(apiSummary.total_participants || 0),
          hard_count: Number(apiSummary.hard_count || 0),
          moderate_count: Number(apiSummary.moderate_count || 0),
          easy_count: Number(apiSummary.easy_count || 0),
        });

        setCurrentPage(1);

        if (rows.length > 0) {
          setSelectedQuestionId(String(rows[0].question_id));
        } else {
          setSelectedQuestionId("");
        }
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setErrorMessage("Analytics data could not be loaded for this assessment.");
      setAnalyticsData([]);
      setSummary({
        total_questions: 0,
        total_responses: 0,
        total_participants: 0,
        hard_count: 0,
        moderate_count: 0,
        easy_count: 0,
      });
      setSelectedQuestionId("");
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistractorAnalysis = async (id) => {
    try {
      setDistractorLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API}/getDistractorAnalysis`,
        {
          assessment_id: id,
        },
      );

      if (res.data?.success) {
        setDistractorData(res.data.data || []);
      } else {
        setDistractorData([]);
      }
    } catch (error) {
      console.error("Error fetching distractor analysis:", error);
      setDistractorData([]);
    } finally {
      setDistractorLoading(false);
    }
  };

  const selectedQuestion = useMemo(() => {
    return analyticsData.find(
      (item) => String(item.question_id) === String(selectedQuestionId),
    );
  }, [analyticsData, selectedQuestionId]);

  const totalPages = Math.ceil(analyticsData.length / ITEMS_PER_PAGE);

  const paginatedBarChartData = useMemo(() => {
    const mapped = analyticsData.map((item, index) => ({
      name: `Q${index + 1}`,
      question_text: item.question_text,
      total_attempts: Number(item.total_attempts || 0),
      correct_attempts: Number(item.correct_attempts || 0),
      difficulty_index: Number(item.difficulty_index || 0), // important fix
      question_id: item.question_id,
    }));

    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;

    return mapped.slice(start, end);
  }, [analyticsData, currentPage]);

  const radarData = useMemo(() => {
    if (!selectedQuestion) return [];

    const totalAttempts = Number(selectedQuestion.total_attempts || 0);
    const maxAttemptMark = Math.max(totalAttempts, 10);

    return [
      {
        subject: "Attempts",
        value: totalAttempts,
        fullMark: maxAttemptMark,
      },
      {
        subject: "Correct Responses",
        value: Number(selectedQuestion.correct_attempts || 0),
        fullMark: maxAttemptMark,
      },
      {
        subject: "Difficulty (%)",
        value: Number(selectedQuestion.difficulty_index || 0) * 100,
        fullMark: 100,
      },
      {
        subject: "Upper Group",
        value: Number(selectedQuestion.upper_group_correct || 0),
        fullMark: maxAttemptMark,
      },
      {
        subject: "Lower Group",
        value: Number(selectedQuestion.lower_group_correct || 0),
        fullMark: maxAttemptMark,
      },
      {
        subject: "Discrimination (%)",
        value: Number(selectedQuestion.discrimination_index || 0) * 100,
        fullMark: 100,
      },
    ];
  }, [selectedQuestion]);

  const groupedDistractorData = useMemo(() => {
    const questionMap = {};

    distractorData.forEach((item) => {
      const qid = item.question_id;
      if (!questionMap[qid]) {
        const matchedQuestion = analyticsData.find(
          (q) => String(q.question_id) === String(qid),
        );

        questionMap[qid] = {
          question_id: qid,
          question_text: matchedQuestion?.question_text || `Question ${qid}`,
          question_no:
            analyticsData.findIndex(
              (q) => String(q.question_id) === String(qid),
            ) + 1,
          answers: [],
        };
      }

      questionMap[qid].answers.push({
        answer_id: item.answer_id,
        answer_text: item.answer_text,
        is_correct: item.is_correct,
        chosen_count: Number(item.chosen_count || 0),
      });
    });

    return Object.values(questionMap).sort(
      (a, b) => Number(a.question_no) - Number(b.question_no),
    );
  }, [distractorData, analyticsData]);

  const formatNumber = (num) => Number(num || 0).toLocaleString();

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className="App analytics-app">
      <Navbar />

      <main className="analytics-page">
        <div className="analytics-workspace">
          <header className="analytics-header">
            <div className="analytics-heading">
              <button
                type="button"
                className="analytics-back-button"
                onClick={() => navigate(-1)}
                aria-label="Back to dashboard"
                title="Back to dashboard"
              >
                <FaArrowLeft size={16} aria-hidden="true" />
              </button>

              <div>
                <span className="analytics-eyebrow">Reporting workspace</span>
                <h1 className="analytics-title">Assessment analytics</h1>
                <p className="analytics-intro">
                  Review question difficulty, discrimination quality, and answer patterns.
                </p>
              </div>
            </div>

            <div className="analytics-header-actions">
              <div className="analytics-assessment-picker">
                <label htmlFor="analytics-assessment-select">
                  Assessment
                </label>

                <Form.Select
                  id="analytics-assessment-select"
                  value={assessmentId}
                  onChange={(e) => setAssessmentId(e.target.value)}
                  aria-label="Choose assessment to analyse"
                >
                  <option value="">Choose an assessment</option>
                  {assessments.map((item) => (
                    <option key={item.assessment_id} value={item.assessment_id}>
                      {item.assessment_name}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <a
                href={analyticsPdf}
                download="MCQ Assessment Analytics Explained.pdf"
                className="analytics-guide"
                title="Download the analytics guide"
              >
                <FaDownload size={13} aria-hidden="true" />
                Analytics guide
              </a>
            </div>
          </header>

          {loading ? (
            <div className="analytics-loading" role="status" aria-live="polite">
              <Spinner animation="border" style={{ color: "#263f86" }} />
              <span>Loading assessment analytics…</span>
            </div>
          ) : errorMessage ? (
            <div className="analytics-empty-state" role="alert">
              <strong>Analytics unavailable</strong>
              <span>{errorMessage}</span>
            </div>
          ) : assessments.length === 0 ? (
            <div className="analytics-empty-state">
              <strong>No assessments available</strong>
              <span>Create and publish an assessment to see analytics here.</span>
            </div>
          ) : (
            <>
              <div className="row g-3 analytics-summary-row">
                <div className="col-md-4">
                  <SummaryCard
                    title="Questions analysed"
                    value={formatNumber(summary.total_questions)}
                  />
                </div>
                <div className="col-md-4">
                  <SummaryCard
                    title="Responses recorded"
                    value={formatNumber(summary.total_responses)}
                  />
                </div>
                <div className="col-md-4">
                  <SummaryCard
                    title="Participants"
                    value={formatNumber(summary.total_participants)}
                  />
                </div>
              </div>

              <div className="row g-4 analytics-primary-grid">
                <div className="col-lg-7">
                  <div
                    className="analytics-card"
                    style={{
                      background: "#fff",
                      borderRadius: "12px",
                      padding: "18px",
                      border: "1px solid #ececec",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                      minHeight: "560px",
                    }}
                  >
                    <h2 className="analytics-card-title">Question difficulty</h2>
                    <p className="analytics-card-description">
                      See how questions are distributed by their difficulty index.
                    </p>

                    <div className="row g-3 analytics-difficulty-summary">
                      <div className="col-md-4">
                        <div className="analytics-difficulty-band analytics-difficulty-band--hard">
                          <strong>{summary.hard_count}</strong>
                          <span>Hard</span>
                          <small>P-value below 0.30</small>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="analytics-difficulty-band analytics-difficulty-band--moderate">
                          <strong>{summary.moderate_count}</strong>
                          <span>Moderate</span>
                          <small>P-value from 0.30 to 0.80</small>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="analytics-difficulty-band analytics-difficulty-band--easy">
                          <strong>{summary.easy_count}</strong>
                          <span>Easy</span>
                          <small>P-value above 0.80</small>
                        </div>
                      </div>
                    </div>

                    <div className="analytics-chart-header">
                      <div>
                        <h3 className="analytics-card-title">Question performance</h3>
                        <p className="analytics-card-description">
                          Attempts and correct responses; labels show each P-value.
                        </p>
                      </div>

                      <div className="analytics-pagination" aria-label="Question chart pagination">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          aria-label="Show previous questions"
                        >
                          Previous
                        </Button>
                        <span className="analytics-page-count" aria-live="polite">
                          Page {currentPage} / {totalPages || 1}
                        </span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={
                            currentPage === totalPages || totalPages === 0
                          }
                          aria-label="Show next questions"
                        >
                          Next
                        </Button>
                      </div>
                    </div>

                    <div className="analytics-chart-canvas" aria-label="Question performance bar chart">
                      <ResponsiveContainer>
                        <BarChart
                          data={paginatedBarChartData}
                          margin={{ top: 45, right: 10, left: 0, bottom: 10 }}
                          barCategoryGap={18}
                        >
                          <CartesianGrid strokeDasharray="4 4" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === "total_attempts")
                                return [<strong>{value}</strong>, "Attempts"];
                              if (name === "correct_attempts")
                                return [<strong>{value}</strong>, "Correct"];
                              if (name === "difficulty_index")
                                return [Number(value).toFixed(2), "P-value"];
                              return [value, name];
                            }}
                            labelFormatter={(label, payload) => {
                              if (payload && payload.length > 0) {
                                return payload[0].payload.question_text;
                              }
                              return label;
                            }}
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "6px",
                              fontWeight: "600",
                            }}
                            itemStyle={{
                              fontWeight: "600",
                            }}
                            labelStyle={{
                              fontWeight: "700",
                            }}
                          />

                          <Bar
                            dataKey="total_attempts"
                            name="Attempts"
                            radius={[4, 4, 0, 0]}
                            fill="#4265b3"
                          />

                          <Bar
                            dataKey="correct_attempts"
                            name="Correct"
                            radius={[4, 4, 0, 0]}
                            fill="#b45309"
                          >
                            <LabelList
                              content={(props) => {
                                const { x, y, width, index } = props;
                                const item = paginatedBarChartData[index];

                                if (!item) return null;

                                return (
                                  <text
                                    x={x + width / 2}
                                    y={y - 8}
                                    textAnchor="middle"
                                    fill="#000"
                                    fontSize="11"
                                    fontWeight="600"
                                  >
                                    {`${Number(
                                      item.difficulty_index || 0,
                                    ).toFixed(2)}`}
                                  </text>
                                );
                              }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="col-lg-5">
                  <div
                    className="analytics-card"
                    style={{
                      background: "#fff",
                      borderRadius: "12px",
                      padding: "18px",
                      border: "1px solid #ececec",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                      minHeight: "640px",
                    }}
                  >
                    <div className="analytics-discrimination-header">
                      <div>
                        <h2 className="analytics-card-title">Question discrimination</h2>
                        <p className="analytics-card-description">
                          Compare how each question performs across learner groups.
                        </p>
                      </div>
                    </div>

                    <Form.Select
                      id="analytics-question-select"
                      className="analytics-question-select"
                      value={selectedQuestionId}
                      onChange={(e) => setSelectedQuestionId(e.target.value)}
                      aria-label="Choose a question to analyse"
                    >
                      <option value="">Choose a question</option>
                      {analyticsData.map((item, index) => (
                        <option key={item.question_id} value={item.question_id}>
                          {`Q${index + 1} - ${item.question_text}`}
                        </option>
                      ))}
                    </Form.Select>

                    <div className="analytics-radar-canvas" aria-label="Selected question metrics radar chart">
                      <ResponsiveContainer>
                        <RadarChart outerRadius="78%" data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{
                              fill: "#334155",
                              fontSize: 11,
                            }}
                            stroke="#00000040"
                          />
                          <PolarRadiusAxis tick={{ fontSize: 10 }} />
                          <Radar
                            name="Question Metrics"
                            dataKey="value"
                            stroke="#263f86"
                            fill="#4265b3"
                            fillOpacity={0.32}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #cfd9e8",
                              borderRadius: "9px",
                            }}
                            labelStyle={{ color: "#000" }}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="row text-center analytics-metric-row">
                      <div className="col-4">
                        <MetricCircle
                          value={selectedQuestion?.upper_group_correct || 0}
                          label="Upper Group Correct Responses"
                        />
                      </div>
                      <div className="col-4">
                        <MetricCircle
                          value={selectedQuestion?.lower_group_correct || 0}
                          label="Lower Group Correct Responses"
                        />
                      </div>
                      <div className="col-4">
                        <MetricCircle
                          value={Number(
                            selectedQuestion?.discrimination_index || 0,
                          ).toFixed(2)}
                          label="Question Discrimination Index"
                        />
                      </div>
                    </div>

                    {selectedQuestion && (
                      <div
                        className="analytics-question-detail"
                        style={{
                          background: "#f8f9fd",
                          borderRadius: "12px",
                          padding: "16px",
                          border: "1px solid #ececec",
                        }}
                      >
                        <div className="mb-3 text-start">
                          <strong style={{ color: "#263f86" }}>
                            Question:{" "}
                          </strong>
                          <span style={{ color: "#555" }}>
                            {selectedQuestion.question_text}
                          </span>
                        </div>

                        <div className="row">
                          <div className="col-6 mb-3 text-start">
                            <small className="text-muted d-block">
                              Difficulty Level
                            </small>
                            <div className="fw-semibold">
                              {selectedQuestion.difficulty_level}
                            </div>
                          </div>

                          <div className="col-6 mb-3 text-start">
                            <small className="text-muted d-block">
                              Quality
                            </small>
                            <div className="fw-semibold">
                              {selectedQuestion.discrimination_quality}
                            </div>
                          </div>

                          <div className="col-6 mb-3 text-start">
                            <small className="text-muted d-block">
                              Difficulty Index
                            </small>
                            <div className="fw-semibold">
                              {selectedQuestion.difficulty_index}
                            </div>
                          </div>

                          <div className="col-6 mb-3 text-start">
                            <small className="text-muted d-block">
                              Correct Responses
                            </small>
                            <div className="fw-semibold">
                              {selectedQuestion.correct_attempts}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row analytics-distractor-section">
                <div className="col-12">
                  <div className="analytics-card analytics-distractor-card">
                    <h2 className="analytics-card-title">Distractor analysis</h2>
                    <p className="analytics-card-description">
                      Review how often learners selected each answer option. Correct answers are highlighted.
                    </p>

                    {distractorLoading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: "180px" }}
                      >
                        <Spinner
                          animation="border"
                          style={{ color: "#263f86" }}
                        />
                      </div>
                    ) : groupedDistractorData.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No distractor analysis data found.
                      </div>
                    ) : (
                      <div className="analytics-distractor-list">
                        {groupedDistractorData.map((question) => (
                          <article
                            className="analytics-distractor-item"
                            key={question.question_id}
                          >
                            <header className="analytics-distractor-question">
                              <span className="analytics-question-number">
                                Q{question.question_no}
                              </span>
                              <p>{question.question_text}</p>
                            </header>

                            <div className="analytics-answer-grid">
                              {question.answers.map((answer, index) => {
                                const isCorrect = Number(answer.is_correct) === 1;

                                return (
                                  <div
                                    className={`analytics-answer-option${
                                      isCorrect
                                        ? " analytics-answer-option--correct"
                                        : ""
                                    }`}
                                    key={answer.answer_id}
                                  >
                                    <div className="analytics-answer-title">
                                      <strong>{String.fromCharCode(65 + index)}.</strong>
                                      <span>{answer.answer_text}</span>
                                    </div>
                                    {isCorrect && (
                                      <span className="analytics-correct-label">
                                        Correct answer
                                      </span>
                                    )}
                                    <div className="analytics-answer-count">
                                      {formatNumber(answer.chosen_count)}
                                      <span>learner selections</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <ScrollToTopButton />
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div className="analytics-summary-card">
      <div className="analytics-summary-value">{value}</div>
      <div className="analytics-summary-label">{title}</div>
    </div>
  );
}

function MetricCircle({ value, label }) {
  return (
    <div className="d-flex flex-column align-items-center">
      <div className="analytics-metric-circle">{value}</div>
      <div className="analytics-metric-label">{label}</div>
    </div>
  );
}

export default Analytics;
